import React from "react";
import {
    SovosBox,
    SovosMenuItem,
    SovosStep,
    SovosStepButton,
    SovosStepLabel,
    SovosStepper,
    SovosTab,
    SovosTabs,
    SovosToolbar,
    SovosToolbarActions,
    SovosToolbarMoreMenu,
    SovosTypography,
} from "mosaic-react";
import { FLOW_STEP_INDEX, STEPS } from "../../store/views/MappingFlow/ColumnMappingCanvas/constants";

const StatusBadge = ({ label, dotColor, bgColor, borderColor, textColor }) => (
    <SovosBox
        sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            px: 1,
            py: 0.25,
            borderRadius: 3,
            bgcolor: bgColor,
            border: "1px solid",
            borderColor,
        }}
    >
        <SovosBox sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: dotColor, flexShrink: 0 }} />
        <SovosTypography variant="caption" sx={{ color: textColor, fontWeight: 600, fontSize: 12 }}>
            {label}
        </SovosTypography>
    </SovosBox>
);

const ColumnMappingFlowHeader = ({
    step,
    frameworkName,
    imported,
    mappingPersisted = false,
    mappingLoading = false,
    actions = [],
    onStepClick,
    activeTab,
    onTabChange,
    showStagingDataTab = false,
    onProceed,
    onEditTemplate,
    onDataProcessingLog,
    onDataChangeLog,
    onDeleteTemplate,
    i18n = (strings) => (Array.isArray(strings) ? strings.join("") : strings),
}) => {
    const showHeader = step !== undefined;
    const showStatus = showHeader && !mappingLoading;
    const proceeded = Boolean(imported || mappingPersisted);

    const statusBadge = () => {
        if (proceeded) {
            return <StatusBadge label={i18n`Proceeded`} dotColor="success.main" bgColor="success.light" borderColor="success.light" textColor="success.dark" />;
        }
        return <StatusBadge label={i18n`Under mapping`} dotColor="warning.main" bgColor="warning.light" borderColor="warning.light" textColor="warning.dark" />;
    };

    if (!showHeader) return null;

    const showStepper = activeTab === "mapping" && !mappingLoading;
    const stepLabels = [i18n`Map entity`, i18n`Map columns`];

    return (
        <SovosBox
            sx={{
                bgcolor: "background.paper",
                boxSizing: "border-box",
                mx: "-24px",
                pb: 0,
                width: "calc(100% + 48px)",
            }}
        >
            <SovosToolbar
                sx={{
                    alignItems: "center",
                    backgroundColor: "transparent !important",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    borderTop: "1px solid",
                    boxSizing: "border-box",
                    display: "flex",
                    minHeight: "72px !important",
                    px: "24px !important",
                    py: 1,
                    width: "100%",
                    "& .sovosToolbarActions": {
                        alignItems: "center",
                        display: "flex",
                        flexShrink: 0,
                        gap: 1,
                        marginLeft: "auto",
                    },
                }}
            >
                <SovosBox sx={{ alignItems: "center", display: "flex", gap: 2, justifyContent: "space-between", width: "100%" }}>
                    <SovosBox sx={{ flex: "1 1 auto", minWidth: 0, overflow: "hidden" }}>
                        <SovosTypography variant="caption" color="primary.main" fontWeight={600} sx={{ display: "block", lineHeight: 1.3, mb: 0.5 }}>
                            {i18n`Files`}
                        </SovosTypography>
                        <SovosBox sx={{ alignItems: "center", display: "flex", gap: 1.5, minWidth: 0, overflow: "hidden" }}>
                            {frameworkName && (
                                <SovosTypography variant="h4" fontWeight={700} noWrap sx={{ lineHeight: 1.2, minWidth: 0 }}>
                                    {frameworkName}
                                </SovosTypography>
                            )}
                            {showStatus && statusBadge()}
                        </SovosBox>
                    </SovosBox>

                    <SovosToolbarActions>
                        {actions.map((action) => action)}
                        <SovosToolbarMoreMenu>
                            <SovosMenuItem onClick={onProceed}>{i18n`Proceed`}</SovosMenuItem>
                            <SovosMenuItem onClick={onEditTemplate}>{i18n`Edit template`}</SovosMenuItem>
                            <SovosMenuItem onClick={onDataProcessingLog}>{i18n`Data processing log`}</SovosMenuItem>
                            <SovosMenuItem onClick={onDataChangeLog}>{i18n`Data change log`}</SovosMenuItem>
                            <SovosMenuItem onClick={onDeleteTemplate}>{i18n`Delete template`}</SovosMenuItem>
                        </SovosToolbarMoreMenu>
                    </SovosToolbarActions>
                </SovosBox>
            </SovosToolbar>

            {showStagingDataTab && (
                <SovosTabs
                    value={activeTab || "mapping"}
                    onChange={(_, value) => onTabChange && onTabChange(value)}
                    sx={{ mb: 1, px: 3, pt: 1.5 }}
                >
                    <SovosTab value="mapping" label={i18n`Mapping`} />
                    <SovosTab value="staging" label={i18n`Staging Data`} />
                </SovosTabs>
            )}

            {showStepper && (
                <SovosStepper activeStep={step - FLOW_STEP_INDEX.ChooseEntity} sx={{ bgcolor: "transparent", pb: 1.5, px: 3 }}>
                    {stepLabels.map((label, idx) => {
                        const stepNum = idx + 1;
                        const isDone = proceeded || stepNum < step;
                        const canNavigate = isDone && onStepClick && idx !== 0;
                        return (
                            <SovosStep key={STEPS[idx] || label} completed={isDone}>
                                {canNavigate ? (
                                    <SovosStepButton onClick={() => onStepClick(stepNum)}>
                                        {label}
                                    </SovosStepButton>
                                ) : (
                                    <SovosStepLabel>{label}</SovosStepLabel>
                                )}
                            </SovosStep>
                        );
                    })}
                </SovosStepper>
            )}
        </SovosBox>
    );
};

export default ColumnMappingFlowHeader;
